// Dollar figures read from the duty engine's answer. Kept apart from
// tariffEngine.ts, which calls the server, so display components can use them
// without pulling in the API client.

/** The engine's `math` block, live (`engineMath`) or saved on a snapshot. */
export interface EngineMath {
  dutyCost?: number;
  additionalDutyDollars?: number;
  baseAmountDollars?: number;
  mpf?: number;
  hmf?: number;
  totalCost?: number;
}

export interface EngineFigures {
  dutyCost: number;
  additional: number;
  baseAmount: number;
  baseDuty: number;
  basePercent: number;
  mpf: number;
  hmf: number;
  totalCost: number;
}

/**
 * Dollar figures from an engine `math` block. The base duty is backed out of
 * the total rather than re-derived from the rate string, so it can never
 * disagree with it.
 */
export const engineFigures = (
  math: EngineMath | null | undefined,
): EngineFigures => {
  const dutyCost = Number(math?.dutyCost) || 0;
  const additional = Number(math?.additionalDutyDollars) || 0;
  const baseAmount = Number(math?.baseAmountDollars) || 0;
  return {
    dutyCost: dutyCost,
    additional: additional,
    baseAmount: baseAmount,
    // Base duty, specific and ad valorem together.
    baseDuty: Math.max(0, dutyCost - additional),
    // The ad valorem part of base duty alone.
    basePercent: Math.max(0, dutyCost - additional - baseAmount),
    mpf: Number(math?.mpf) || 0,
    hmf: Number(math?.hmf) || 0,
    totalCost: Number(math?.totalCost) || 0,
  };
};

const isFreeRate = (v: unknown) =>
  String(v ?? '')
    .trim()
    .toLowerCase() === 'free';

const isBaseRow = (r: any) =>
  r?.type === 'Base Duty' || r?.type === 'Base Duty (Column 2)';

/**
 * The Base Duty Rate to show for a result.
 *
 * When a split puts some of the value on a line whose base duty is Free (a
 * metal-content split), the stated rate overstates what was charged, so the
 * effective rate is shown instead: the engine's ad valorem base dollars over
 * the entry value.
 */
export const effectiveBaseDutyRate = (
  baseDutyRate: string,
  lines: any[],
  math: EngineMath | null | undefined,
  productValue: number,
): string => {
  if (!Array.isArray(lines) || lines.length === 0 || isFreeRate(baseDutyRate)) {
    return baseDutyRate;
  }
  const someLineFree = lines.some((ln: any) =>
    (Array.isArray(ln?.rows) ? ln.rows : []).some(
      (r: any) => isBaseRow(r) && isFreeRate(r?.rate),
    ),
  );
  const value = Number(productValue) || 0;
  if (!someLineFree || !math || value <= 0) {
    return baseDutyRate;
  }
  const pct = (engineFigures(math).basePercent / value) * 100;
  return `${pct
    .toFixed(2)
    .replace(/\.00$/, '')
    .replace(/(\.\d)0$/, '$1')}%`;
};
